/**
 * Slide-out drawer showing every position one subscriber has closed for the
 * basket being analyzed. Opened from the subscriber roster on the
 * MarketplaceAnalyticsDetailPage.
 *
 * Pure read-only — no actions on rows. "View as" lives on the parent row.
 */
import React, { useEffect, useState } from 'react';
import { exitTypeLabel } from '../../utils/exitTypeLabel';
import FocusLock from 'react-focus-lock';
import { X as XIcon, Download } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import Button from '../ui/Button';
import marketplaceAnalyticsService from '../../services/marketplaceAnalyticsService';
import { downloadFile } from '../../utils/downloadFile';
import { formatInr, pnlClass } from '../../utils/platformFormat';
import { cn } from '../../utils/cn';
import type {
  MarketplacePosition,
  MarketplaceSubscriberRow,
} from '../../types/marketplaceAnalytics';

interface Props {
  basketId: string;
  subscriber: MarketplaceSubscriberRow;
  onClose: () => void;
}

const SubscriberDrilldownDrawer: React.FC<Props> = ({ basketId, subscriber, onClose }) => {
  const [positions, setPositions] = useState<MarketplacePosition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    marketplaceAnalyticsService
      .subscriberPositions(basketId, subscriber.user_id)
      .then((resp) => { if (!cancelled) setPositions(resp.positions || []); })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Failed to load positions'); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [basketId, subscriber.user_id]);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const onExportCsv = () => {
    const header = [
      'closed_date', 'symbol', 'action', 'quantity',
      'entry_price', 'exit_price', 'pnl', 'exit_type',
      'broker', 'client_id', 'strategy_id', 'entry_filled_at',
    ];
    const escape = (v: any): string => {
      let s = v == null ? '' : String(v);
      if (s && /^[=+\-@]/.test(s)) s = `'${s}`;
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      header.join(','),
      ...positions.map((p) => [
        p.closed_date, p.symbol, p.action, p.quantity,
        p.entry_price, p.exit_price, p.pnl, p.exit_type,
        p.broker, p.client_id, p.strategy_id, p.entry_filled_at,
      ].map(escape).join(',')),
    ];
    const safeName = subscriber.user_id.slice(0, 8);
    downloadFile(`basket-${basketId}-${safeName}-trades.csv`, lines.join('\n'),
                 'text/csv;charset=utf-8');
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
        aria-hidden="true"
      />
      {/* Panel — FocusLock traps Tab inside the drawer so keyboard users can't
          escape into the obscured page behind. tabIndex={-1} + data-autofocus
          lands initial focus on the panel container, not the first button. */}
      <FocusLock returnFocus autoFocus className="contents">
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Trades for ${subscriber.full_name || subscriber.email}`}
        tabIndex={-1}
        data-autofocus
        className="fixed top-0 right-0 h-full w-full sm:w-[640px] bg-white dark:bg-gray-900 shadow-2xl z-50 flex flex-col focus:outline-none"
      >
        <header className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-lg font-semibold text-gray-900 dark:text-white truncate">
              {subscriber.full_name || '(unnamed)'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {subscriber.email} · {positions.length} trade{positions.length === 1 ? '' : 's'} for this basket
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onExportCsv}
              leftIcon={<Download className="h-4 w-4" />}
              disabled={positions.length === 0}
            >
              CSV
            </Button>
            <button
              type="button"
              onClick={onClose}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label="Close"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Loading trades…</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 px-6 py-4 text-sm text-red-800 dark:text-red-200">
              {error}
            </div>
          ) : positions.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              No closed trades for this basket.
            </div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              {positions.map((p) => (
                <li key={p.position_id} className="px-6 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-xs text-gray-900 dark:text-white truncate">{p.symbol}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {p.closed_date} · {p.action} {p.quantity}
                        {' · '}{p.broker}:{p.client_id}
                        {p.exit_type ? ` · ${exitTypeLabel(p.exit_type)}` : ''}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={cn('font-semibold tabular-nums', pnlClass(p.pnl))}>
                        {formatInr(p.pnl)}
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums">
                        {p.entry_price.toFixed(2)} → {p.exit_price.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
      </FocusLock>
    </>
  );
};

export default SubscriberDrilldownDrawer;
